import { IChangeRequestService } from "../../changeRequests/services/ChangeRequestService";
import { ISpaceService } from "../services/SpaceService";
import { useEffect, useState } from "react";

/** What a space card shows beside the listing: pages, push policy, proposed change requests (each unknown until it loads). */
export interface SpaceFigures {
    pageCount?: number;
    pushPolicy?: "direct" | "propose";
    proposed?: number;
}

/**
 * The figures of the listed spaces: one `GET api/agentic/v1/spaces` (pages, push policy) and one proposed-changesets count per space
 * (`GET spaces/{id}/changesets?state=proposed&take=200`, the rail badge's call). Failures are silent: a figure the server cannot give
 * simply stays out of the card.
 */
export function useSpaceFigures(
    spaceIds: number[],
    spaces: ISpaceService,
    changeRequests: IChangeRequestService
): Record<number, SpaceFigures> {
    const [figures, setFigures] = useState<Record<number, SpaceFigures>>({});
    const key = spaceIds.join(",");

    useEffect(() => {
        if (!key) return;
        const ids = key.split(",").map(Number);
        let alive = true;
        const merge = (id: number, part: SpaceFigures) =>
            alive && setFigures((current) => ({ ...current, [id]: { ...current[id], ...part } }));

        spaces
            .getSpaceSummaries()
            .then((list) => {
                for (const s of list) if (ids.includes(s.id)) merge(s.id, { pageCount: s.pageCount, pushPolicy: s.pushPolicy });
            })
            .catch(() => undefined);
        for (const id of ids) {
            changeRequests
                .list(id, "proposed", 200)
                .then((items) => merge(id, { proposed: items.length }))
                .catch(() => undefined);
        }
        return () => {
            alive = false;
        };
        // the services are stable instances; the ids are the key
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return figures;
}
